<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { queueMonitorGetSqsMetrics } from '@/api/cfes/queueMonitoring'

const REFRESH_INTERVAL_MS = 15_000

const sqsMetricsHeaders = [
  { title: 'Queue Name', align: 'start', key: 'queueName' },
  { title: 'Available', align: 'center', key: 'approximateNumberOfMessages' },
  { title: 'In-Flight', align: 'center', key: 'approximateNumberOfMessagesNotVisible' },
  { title: 'Delayed', align: 'center', key: 'approximateNumberOfMessagesDelayed' },
  { title: 'Status', align: 'center', key: 'status', sortable: false },
]

const sqsMetrics = ref([])
const sqsLoading = ref(false)
let sqsTimer = null

const normalizeMetrics = (response) => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.results)) return response.results
  return []
}

const fetchSqsMetrics = async () => {
  if (sqsLoading.value) return

  sqsLoading.value = true
  try {
    sqsMetrics.value = normalizeMetrics(await queueMonitorGetSqsMetrics())
  } catch (error) {
    console.error('Failed to load SQS metrics', error)
  } finally {
    sqsLoading.value = false
  }
}

onMounted(() => {
  fetchSqsMetrics()
  sqsTimer = window.setInterval(fetchSqsMetrics, REFRESH_INTERVAL_MS)
})

onBeforeUnmount(() => {
  if (sqsTimer !== null) window.clearInterval(sqsTimer)
})
</script>

<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">Queue Monitoring</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>

    <v-card class="section-card">
      <v-card-title class="section-title">SQS Metrics</v-card-title>
      <v-data-table
        :headers="sqsMetricsHeaders"
        :items="sqsMetrics"
        :loading="sqsLoading"
        :items-per-page="sqsMetrics.length || 10"
        hide-default-footer
        no-data-text="No SQS metrics available"
        class="elevation-2 monitoring-table"
      >
        <template #item.approximateNumberOfMessages="{ item }">
          {{ item.approximateNumberOfMessages ?? '-' }}
        </template>
        <template #item.approximateNumberOfMessagesNotVisible="{ item }">
          {{ item.approximateNumberOfMessagesNotVisible ?? '-' }}
        </template>
        <template #item.approximateNumberOfMessagesDelayed="{ item }">
          {{ item.approximateNumberOfMessagesDelayed ?? '-' }}
        </template>
        <template #item.status="{ item }">
          <v-chip
            :color="item.error ? 'error' : 'success'"
            size="small"
            variant="flat"
            :title="item.error || 'SQS metrics loaded successfully'"
          >
            {{ item.error ? 'Error' : 'OK' }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.page-shell {
  padding: 8px 18px 28px;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.title {
  font-size: 1.1rem;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  padding: 6px 10px 14px;
}

.section-title {
  padding: 6px 4px;
  font-weight: 700;
}

.monitoring-table :deep(.v-data-table__wrapper) {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.monitoring-table :deep(tbody tr:nth-child(odd)) {
  background-color: #f9fafb;
}

.monitoring-table :deep(tbody tr:hover) {
  background-color: #eef2ff;
}
</style>
